import {
  isPrivateAccessTokenParameter,
  removePrivateAccessTokenParameters,
  scrubPrivateAccessTokenFromHash,
} from "@/lib/urlPrivacy";

const SESSION_KEY_PREFIX = "hg_private_access_token:";
// UUIDs from invites, activation packs and password unlocks, plus the 64-char
// hex tokens minted by the share drawer's Access Links (genie-access/create).
const PRIVATE_ACCESS_TOKEN_PATTERN =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{64})$/i;

const memoryTokens = new Map<string, string>();

const sessionKey = (scope: string) => `${SESSION_KEY_PREFIX}${scope}`;
const persistedKey = (scope: string) => `${SESSION_KEY_PREFIX}persisted:${scope}`;

export const getPrivateAccessTokenScope = (pathname: string): string =>
  `path:${pathname || "/"}`;

const readTokenParameter = (params: URLSearchParams): string | undefined => {
  for (const [name, value] of params.entries()) {
    if (
      isPrivateAccessTokenParameter(name) &&
      PRIVATE_ACCESS_TOKEN_PATTERN.test(value)
    ) {
      return value;
    }
  }
  return undefined;
};

const readTokenFromHash = (hash: string): string | undefined => {
  if (!hash) return undefined;
  const rawHash = hash.slice(1);
  const routeQueryIndex = rawHash.startsWith("/") ? rawHash.indexOf("?") : -1;
  if (routeQueryIndex >= 0) {
    return readTokenParameter(
      new URLSearchParams(rawHash.slice(routeQueryIndex + 1)),
    );
  }
  if (!rawHash.includes("=")) return undefined;
  return readTokenParameter(new URLSearchParams(rawHash));
};

const loadStoredToken = (scope: string): string | undefined => {
  const inMemory = memoryTokens.get(scope);
  if (inMemory) return inMemory;

  try {
    const stored = window.sessionStorage.getItem(sessionKey(scope));
    if (stored && PRIVATE_ACCESS_TOKEN_PATTERN.test(stored)) {
      memoryTokens.set(scope, stored);
      return stored;
    }
  } catch {
    // Some embedded/private browsing contexts disable sessionStorage. The
    // in-memory fallback still keeps the token available across React renders.
  }
  return loadPersistedToken(scope);
};

const loadPersistedToken = (scope: string): string | undefined => {
  try {
    const raw = window.localStorage.getItem(persistedKey(scope));
    if (!raw) return undefined;
    const { token, expiresAt } = JSON.parse(raw) as { token?: unknown; expiresAt?: unknown };
    const isLive =
      typeof token === "string" &&
      PRIVATE_ACCESS_TOKEN_PATTERN.test(token) &&
      typeof expiresAt === "string" &&
      Date.parse(expiresAt) > Date.now();
    if (!isLive) {
      window.localStorage.removeItem(persistedKey(scope));
      return undefined;
    }
    memoryTokens.set(scope, token);
    return token;
  } catch {
    return undefined;
  }
};

const storeToken = (scope: string, token: string) => {
  memoryTokens.set(scope, token);
  try {
    window.sessionStorage.setItem(sessionKey(scope), token);
  } catch {
    // The in-memory copy is sufficient for the lifetime of this page.
  }
};

const scrubCurrentBrowserUrl = () => {
  const current = new URL(window.location.href);
  const queryChanged = removePrivateAccessTokenParameters(current.searchParams);
  const cleanHash = scrubPrivateAccessTokenFromHash(current.hash);
  const hashChanged = cleanHash !== current.hash;
  if (!queryChanged && !hashChanged) return;

  current.hash = cleanHash;
  try {
    window.history.replaceState(
      window.history.state,
      "",
      `${current.pathname}${current.search}${current.hash}`,
    );
  } catch {
    // Restricted iframes can reject history writes; the token is already captured.
  }
};

/**
 * Captures a private bearer/capability synchronously, scopes it to the current
 * experience, then removes it from the address bar before analytics effects
 * run. Fragment links are preferred; legacy query links remain compatible.
 */
export const capturePrivateAccessToken = (): string | undefined => {
  if (typeof window === "undefined") return undefined;

  const scope = getPrivateAccessTokenScope(window.location.pathname);

  const queryParams = new URLSearchParams(window.location.search);
  // Prefer the fragment when both formats are present because new links place
  // the bearer there specifically to keep it out of HTTP requests/referrers.
  const urlToken =
    readTokenFromHash(window.location.hash) ?? readTokenParameter(queryParams);

  if (urlToken) {
    // `access_token` is also Supabase Auth's implicit-flow parameter. Leave
    // non-UUID values in place so detectSessionInUrl can consume them; only a
    // recognized private-access capability belongs to this scrubber.
    scrubCurrentBrowserUrl();
    storeToken(scope, urlToken);
    return urlToken;
  }
  return loadStoredToken(scope);
};

/**
 * Keeps a token issued by a password unlock on this device until its grant
 * expires, scoped to the current experience like a captured link token.
 */
export const rememberPrivateAccessToken = (token: string, expiresAt: string) => {
  if (typeof window === "undefined" || !PRIVATE_ACCESS_TOKEN_PATTERN.test(token)) return;
  const scope = getPrivateAccessTokenScope(window.location.pathname);
  storeToken(scope, token);
  try {
    window.localStorage.setItem(persistedKey(scope), JSON.stringify({ token, expiresAt }));
  } catch {
    // Storage-restricted contexts keep the session copy only.
  }
};

export const addPrivateAccessTokenFragment =(url: URL, token: string): URL => {
  if (!PRIVATE_ACCESS_TOKEN_PATTERN.test(token)) return url;
  const fragment = new URLSearchParams({ access_token: token });
  url.hash = fragment.toString();
  return url;
};
